import Udesk from 'Udesk';
import UdeskLocales from 'UdeskLocales';
import React, { useState, useMemo, useCallback, FC, memo } from 'react';
import { useRequest } from 'ahooks';
import { Button, FormBuilder, Row, Col, Space, message } from 'udesk-ui';
import type { FormProps } from 'udesk-ui/lib/form-builder/types/form';
import Page from 'Component/common/udesk-qa-page-layout';
import RequestParams from './components/RequestParams';
import ParamsList from './components/ParamsList';
import InterfaceTest from './components/InterfaceTest';
import styled from 'styled-components';
import { postOuterApiConfigs } from 'src/api/outerApiConfigs';
import { getOuterApiConfigsById, putOuterApiConfigsById } from 'src/api/outerApiConfigs/{id}';
import { FormValues } from './types';
import { transform, transformBack } from './utils';

const Wrapper = styled.div`
    height: 100%;
    overflow: auto;
`;

interface TemplateProps {
    match?: {
        params?: Record<string, string>;
    };
    history?: any;
}

const Template: FC<TemplateProps> = memo((props) => {
    const { match } = props;

    const id = match?.params?.id ? +match?.params?.id : undefined;

    const locales = UdeskLocales['current'];

    const [baseForm] = FormBuilder.useForm();

    const [initialValues, setInitialValues] = useState<FormValues>();

    const [curFormValues, setCurFormValues] = useState<FormValues>();

    const { loading: getLoading } = useRequest(
        () =>
            getOuterApiConfigsById({
                segments: {
                    id: id!,
                },
            }),
        {
            ready: !!id,
            onSuccess: (res) => {
                const data: FormValues = {
                    ...initialValues,
                    ...transformBack(res.data || {}),
                };

                setDataSourceList(data.inspectDataSourceList);

                setInitialValues(data);

                setCurFormValues(data);

                baseForm.resetFields();
            },
        }
    );

    const onValuesChange = (_, values: FormValues) => {
        setCurFormValues(values);
        setIsTestSuccess(false);
    };

    const onCancel = useCallback(() => {
        let routeOptions: any = {
            history: props.history,
            routeName: 'externalInterface',
            state: {},
        };
        Udesk.ui.routing.transitionTo(routeOptions);
    }, [props.history]);

    const [isTestSuccess, setIsTestSuccess] = useState(false);

    const onTest = (success?: boolean) => {
        setIsTestSuccess(!!success);
    };

    const onFinish = useCallback(
        (values: FormValues) => {
            baseForm.validateFields().then(() => {
                if (!isTestSuccess) {
                    message.error(/* 未测试通过 */UdeskLocales['current'].pages.semanticIntelligence.externalInterface.detail.index.testNotPassed);
                    return;
                }

                const params = transform(values);

                if (id) {
                    putOuterApiConfigsById(params, { segments: { id } }).then(() => {
                        message.success(
                            /* 保存成功 */ UdeskLocales['current'].pages.semanticIntelligence
                                .externalInterface.detail.index.saveSuccessful
                        );
                        onCancel();
                    });
                } else {
                    postOuterApiConfigs(params).then(() => {
                        message.success(
                            /* 保存成功 */ UdeskLocales['current'].pages.semanticIntelligence
                                .externalInterface.detail.index.saveSuccessful
                        );
                        onCancel();
                    });
                }
            });
        },
        [baseForm, id, isTestSuccess, onCancel]
    );

    const [dataSourceList, setDataSourceList] = useState<number[]>();

    const config: FormProps = useMemo(() => {
        return {
            form: baseForm,
            customComponents: {
                RequestParams,
                ParamsList,
            },
            initialValues,
            onValuesChange,
            onFinish,
            fields: [
                {
                    name: 'apiName',
                    label: /* 接口名称 */ UdeskLocales['current'].pages.semanticIntelligence
                        .externalInterface.detail.index.interfaceName,
                    type: 'Input',
                    rules: [
                        {
                            required: true,
                        },
                    ],
                    props: {
                        placeholder:
                            /* 请输入接口名称 */ UdeskLocales['current'].pages.semanticIntelligence
                                .externalInterface.detail.index.pleaseEnterInterfaceName,
                        style: {
                            width: 300,
                        },
                    },
                },
                {
                    name: 'apiDesc',
                    label: /* 接口描述 */ UdeskLocales['current'].pages.semanticIntelligence
                        .externalInterface.detail.index.interfaceDescription,
                    type: 'TextArea',
                    props: {
                        autoSize: true,
                        placeholder:
                            /* 请输入接口描述 */ UdeskLocales['current'].pages.semanticIntelligence
                                .externalInterface.detail.index.pleaseEnterInterfaceDescription,
                        style: {
                            width: 300,
                        },
                    },
                    rules: [
                        {
                            required: true,
                        },
                    ],
                },
                {
                    name: 'status',
                    label: /* 有效性 */ UdeskLocales['current'].pages.semanticIntelligence
                        .externalInterface.detail.index.validity,
                    type: 'Select',
                    rules: [
                        {
                            required: true,
                        },
                    ],
                    props: {
                        placeholder:
                            /* 请选择有效性 */ UdeskLocales['current'].pages.semanticIntelligence
                                .externalInterface.detail.index.pleaseSelectEffectivity,
                        options: [
                            {
                                label: /* 有效 */ UdeskLocales['current'].pages.semanticIntelligence
                                    .externalInterface.detail.index.effective,
                                value: 1,
                            },
                            {
                                label: /* 无效 */ UdeskLocales['current'].pages.semanticIntelligence
                                    .externalInterface.detail.index.invalid,
                                value: 0,
                            },
                        ],
                        style: {
                            width: 300,
                        },
                    },
                },
                {
                    name: 'inspectDataSourceList',
                    label: /* 数据源 */ UdeskLocales['current'].pages.semanticIntelligence
                        .externalInterface.detail.index.dataSource,
                    type: 'Select',
                    rules: [
                        {
                            required: true,
                        },
                    ],
                    props: {
                        placeholder:
                            /* 请选择数据源 */ UdeskLocales['current'].pages.semanticIntelligence
                                .externalInterface.detail.index.pleaseSelectADataSource,
                        mode: 'multiple',
                        maxTagCount: 2,
                        options: Udesk.enums.inspectDataSources
                            .filter((item) =>
                                [
                                    Udesk.enums.inspectDataSources.voiceCall.id,
                                    Udesk.enums.inspectDataSources.textDialogue.id,
                                    Udesk.enums.inspectDataSources.ticket.id,
                                    Udesk.enums.inspectDataSources.videoCall.id,
                                    Udesk.enums.inspectDataSources.survey.id,
                                    Udesk.enums.inspectDataSources.eComment.id,
                                    Udesk.enums.inspectDataSources.mComment.id,
                                    Udesk.enums.inspectDataSources.taobao.id,
                                    Udesk.enums.inspectDataSources.wechatDialogue.id,
                                    Udesk.enums.inspectDataSources.wechatRadio.id,
                                ].includes(item.id)
                            )
                            .map((i) => ({ label: i.name, value: i.id })),
                        onChange: (list: number[]) => {
                            setDataSourceList(list);
                        },
                        style: {
                            width: 300,
                        },
                    },
                },
                {
                    name: 'requestMethod',
                    label: /* 请求方法 */ UdeskLocales['current'].pages.semanticIntelligence
                        .externalInterface.detail.index.requestMethod,
                    type: 'Select',
                    rules: [
                        {
                            required: true,
                        },
                    ],
                    props: {
                        placeholder:
                            /* 请选择请求方法 */ UdeskLocales['current'].pages.semanticIntelligence
                                .externalInterface.detail.index.pleaseSelectARequestMethod,
                        options: [
                            {
                                label: 'GET',
                                value: 1,
                            },
                            {
                                label: 'POST',
                                value: 2,
                            },
                        ],
                        style: {
                            width: 300,
                        },
                    },
                },
                {
                    name: 'requestUrl',
                    label: /* 请求地址 */ UdeskLocales['current'].pages.semanticIntelligence
                        .externalInterface.detail.index.requestAddress,
                    type: 'Input',
                    rules: [
                        {
                            required: true,
                        },
                    ],
                    props: {
                        placeholder:
                            /* 请输入请求地址 */ UdeskLocales['current'].pages.semanticIntelligence
                                .externalInterface.detail.index.pleaseEnterTheRequestAddress,
                        style: {
                            width: 300,
                        },
                    },
                },
                {
                    name: 'requestParams',
                    label: /* 请求参数 */ UdeskLocales['current'].pages.semanticIntelligence
                        .externalInterface.detail.index.requestParameters,
                    type: 'RequestParams',
                    required: true,
                    props: {
                        dataSourceList,
                    },
                    rules: [
                        {
                            required: true,
                            validator(rule, value: FormValues['requestParams'], callback) {
                                if (
                                    value?.requestBodyList?.length ||
                                    value?.requestHeaderList?.length ||
                                    value?.requestQueryList?.length
                                ) {
                                    return Promise.resolve();
                                } else {
                                    return Promise.reject(
                                        /* 请添加请求参数 */ UdeskLocales['current'].pages
                                            .semanticIntelligence.externalInterface.detail.index
                                            .pleaseAddRequestParameters
                                    );
                                }
                            },
                        },
                    ],
                },
                {
                    name: 'responseBodyList',
                    label: /* 返回参数 */ UdeskLocales['current'].pages.semanticIntelligence
                        .externalInterface.detail.index.returnParameter,
                    type: 'ParamsList',
                    required: true,
                    props: {
                        showTitle: false,
                    },
                    rules: [
                        {
                            required: true,
                            validator(rule, value: FormValues['responseBodyList'], callback) {
                                if (value?.length) {
                                    return Promise.resolve();
                                } else {
                                    return Promise.reject(
                                        /* 请添加返回参数 */ UdeskLocales['current'].pages
                                            .semanticIntelligence.externalInterface.detail.index
                                            .pleaseAddAReturnParameter
                                    );
                                }
                            },
                        },
                    ],
                },
            ],
            footerRender: false,
            labelCol: { span: 4 },
            wrapperCol: { span: 20 },
        };
    }, [baseForm, dataSourceList, initialValues, onFinish]);

    return (
        <Page
            pageBodyClassName="wechat-staff-center-page-index"
            title={
                id
                    ? /* 编辑外部接口 */ UdeskLocales['current'].pages.semanticIntelligence
                          .externalInterface.detail.index.editExternalInterface
                    : /* 新建外部接口 */ UdeskLocales['current'].pages.semanticIntelligence
                          .externalInterface.detail.index.newExternalInterface
            }
            padding={true}
            onBack={onCancel}
        >
            <Wrapper>
                <FormBuilder {...config} />

                <Row style={{ marginBottom: 20 }}>
                    <Col offset={4}>
                        <InterfaceTest
                            data={transform(curFormValues)}
                            isTestSuccess={isTestSuccess}
                            onTest={onTest}
                        />
                    </Col>
                </Row>

                <Row>
                    <Col offset={4}>
                        <Space>
                            <Button loading={getLoading && { delay: 200 }} onClick={onCancel}>
                                {locales.labels.cancel}
                            </Button>
                            <Button
                                loading={getLoading && { delay: 200 }}
                                type="primary"
                                onClick={() => baseForm.submit()}
                            >
                                {locales.labels.save}
                            </Button>
                        </Space>
                    </Col>
                </Row>
            </Wrapper>
        </Page>
    );
});

class Component extends React.Component {
    render() {
        return <Template {...this.props} />;
    }
}

export default Udesk.react.udeskify(Component);
